import React, { useState } from 'react';
import { ImageUploaderProps, ProductImage } from '../../../types';
import { UploadButton } from '../atoms';
import Icon from '../../atoms/Icon';
import './ImageUploader.css';
import { v4 as uuidv4 } from 'uuid';

const ImageUploader: React.FC<ImageUploaderProps> = ({
  images,
  onImagesChange,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    setIsUploading(true);
    
    const newImages: ProductImage[] = [];
    const fileArray = Array.from(e.target.files);
    
    console.log('Files selected:', fileArray.map(f => ({name: f.name, type: f.type, size: f.size})));
    
    // Create local URLs for preview and store the file object for later upload
    fileArray.forEach((file, index) => {
      // Create a unique ID for this image
      const imageId = uuidv4();
      
      // Create a blob URL for preview
      const imageUrl = URL.createObjectURL(file);
      
      // Determine if this should be the primary image
      const isPrimary = images.length === 0 && index === 0;
      
      // Create a clone of the file to ensure it's properly stored
      // This helps avoid issues with the file object being detached
      const fileClone = new File([file], file.name, { type: file.type });
      
      console.log(`Processing file: ${file.name}, size: ${file.size}, type: ${file.type}`);
      
      newImages.push({
        id: imageId,
        url: imageUrl,
        alt: file.name,
        isPrimary,
        file: fileClone // Store the cloned File object
      });
    });
    
    onImagesChange([...images, ...newImages]);
    setIsUploading(false);
  };

  const handleRemoveImage = (id: string) => {
    const updatedImages = images.filter(img => img.id !== id);
    
    // If we removed the primary image, set the first remaining image as primary
    if (images.find(img => img.id === id)?.isPrimary && updatedImages.length > 0) {
      updatedImages[0].isPrimary = true;
    }
    
    onImagesChange(updatedImages);
  };

  const handleSetPrimary = (id: string) => {
    const updatedImages = images.map(img => ({
      ...img,
      isPrimary: img.id === id
    }));
    
    onImagesChange(updatedImages);
  };

  return (
    <div className={`image-uploader ${className}`.trim()}>
      <div className="image-uploader-header">
        <h3>Product Images</h3>
        <UploadButton 
          onChange={handleImageUpload} 
          multiple 
          accept="image/*"
        >
          <Icon type="upload" size="small" />
          Upload Images
        </UploadButton>
      </div>
      
      {isUploading && (
        <div className="image-uploader-loading">
          Uploading images...
        </div>
      )}
      
      <div className="image-grid">
        {images.map(image => (
          <div 
            key={image.id} 
            className={`image-item ${image.isPrimary ? 'primary' : ''}`}
          >
            <img src={image.url} alt={image.alt} />
            <div className="image-actions">
              {!image.isPrimary && (
                <button 
                  className="image-action-button set-primary-button" 
                  onClick={() => handleSetPrimary(image.id)}
                  title="Set as primary image"
                >
                  <Icon type="heart" size="small" />
                </button>
              )}
              <button 
                className="image-action-button remove-button" 
                onClick={() => handleRemoveImage(image.id)}
                title="Remove image"
              >
                <Icon type="delete" size="small" />
              </button>
            </div>
            {image.isPrimary && (
              <div className="primary-badge">Primary</div>
            )}
          </div>
        ))}
      </div>
      
      {images.length === 0 && !isUploading && (
        <div className="no-images">
          <Icon type="image" size="large" />
          <p>No images uploaded yet</p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
